/**
 * @module AuroraTemplates
 * @description Singleton module that manages Aurora Templates.
 */
const AuroraTemplates = (function () {
  /**
   * An object representing the module's settings.
   *
   * @private
   * @type {object} settings
   * @description The initial settings for the singleton object.
   */
  const settings = {
    name: "aurora-templates.js",
    debugging: true,
    isSiteFrench: AuroraUtils.isSiteFrench(),
  };

  /**
   * Enable logging for the module.
   *
   * @type {boolean}
   */
  const LOGGING = true;

  const assets = window.theme.assets || {};

  const cartLineItem = async (lineItem = {}, cartAdditionalData = {}) => {
    const { image, key, url } = lineItem;

    const quantity = parseInt(lineItem.quantity);
    const variantID = lineItem.variant_id;
    const variantInventory = parseInt(lineItem.properties._variant_inventory);
    const lineItemAdditionalData = cartAdditionalData?.[lineItem?.id] || {};
    const orderLimit = AuroraUtils.getResolvedOrderLimitFromLineItem(lineItem, lineItemAdditionalData, { fallbackToInventory: true });
    const explicitOrderLimit = AuroraUtils.getResolvedOrderLimitFromLineItem(lineItem, lineItemAdditionalData, { fallbackToInventory: false });
    const productOverLimit = orderLimit && quantity > orderLimit ? true : false;
    const productHandle = lineItem?.handle || "";
    const productTitle = AuroraUtils.getResolvedProductTitleFromLineItem(lineItem, lineItemAdditionalData, {
      isSiteFrench: settings.isSiteFrench,
    });

    const disableStepperDecrease = quantity === 0 ? true : false;
    const disableStepperIncrease = quantity === variantInventory || (orderLimit && quantity >= orderLimit) ? true : false;
    const isBundleGift = lineItem.properties?._is_bundle_gift ?? false;

    LOGGING &&
      console.log("[ cartLineItem() :: order limit, quantity, variant inventory, product over limit...", {
        orderLimit,
        quantity,
        variantInventory,
        productOverLimit,
      });

    const language = {
      button: {
        remove: settings.isSiteFrench ? "Supprimer" : "Remove",
        moveToWishlist: settings.isSiteFrench ? "Ajouter à la liste de souhaits" : "Add to Wishlist",
        decrease: settings.isSiteFrench ? "Diminuer" : "Decrease",
        increase: settings.isSiteFrench ? "Augmenter" : "Increase",
      },
    };

    const productExtras = cartLineItemProductExtras(lineItem, cartAdditionalData);
    const productDiscounts = cartLineItemProductDiscounts(lineItem);
    const productOverLimitMessage = cartLineItemProductOverLimitMessage(lineItem, lineItemAdditionalData, productOverLimit, quantity, explicitOrderLimit);
    const productPrice = cartLineItemProductPrice(lineItem);
    const productUrl = window.theme.shop.url + lineItem.url;
    const productId = lineItem.product_id;

    const quantitySelector = isBundleGift
      ? `<span></span><span class="cart-line-item__stepper-input">${quantity}</span><span></span>`
      : `
      <button 
        class="cart-line-item__stepper-button decrease js--cart-line-item-stepper-button" 
        name="decrease" 
        type="button" 
        ${disableStepperDecrease ? "disabled" : ""}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 12H16" stroke="#828282" stroke-width="1.5" stroke-linecap="square"></path>
        </svg>
      </button>
      <input
        class="cart-line-item__stepper-input"
        data-quantity-variant-id="${variantID}"
        data-cart-line-item-key="${key}"
        type="number"
        name="updates[]"
        value="${quantity}"
        min="0"
        max="${orderLimit}"
        max_available="${variantInventory}"
        step="1"
        aria-label="${productTitle}"
      >
      <button 
        class="cart-line-item__stepper-button increase js--cart-line-item-stepper-button" 
        name="increase" 
        type="button" 
        ${disableStepperIncrease ? "disabled" : ""}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 8V16M8 12H16" stroke="#828282" stroke-width="1.5" stroke-linecap="square"></path>
        </svg>
      </button>
      `;

    return `
      <div
        class="cart-line-item js--cart-line-item dynamic"
        id="cart-line-item--${key}"
        data-key="${key}"
        data-product-handle="${productHandle}"
      >

        <div class="cart-line-item__image">
          <a class="cart-line-item__image-link" href="${url}" target="_self" title="${productTitle}">
            <img loading="lazy" src="${image}" alt="${productTitle}" />
          </a>
        </div>

        <div class="cart-line-item__content">
          <div class="cart-line-item__product-info">
            <strong class="cart-line-item__product-title">
              <a class="cart-line-item__product-title-link" href="${url}" target="_self" title="${productTitle}">${productTitle}</a>
            </strong>
            ${productExtras}
            <div class="cart-line-item__controls">
              <button class="cart-line-item__controls-button cart-line-item__controls-button--remove js--cart-line-item-remove" type="button">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6.33333 1.00098H11.6667M1 3.66764H17M15.2222 3.66764L14.5989 13.0181C14.5053 14.421 14.4586 15.1225 14.1556 15.6543C13.8888 16.1226 13.4864 16.499 13.0015 16.734C12.4506 17.001 11.7476 17.001 10.3416 17.001H7.65836C6.25236 17.001 5.54936 17.001 4.99852 16.734C4.51356 16.499 4.11119 16.1226 3.84443 15.6543C3.54143 15.1225 3.49467 14.421 3.40114 13.0181L2.77778 3.66764M7.22222 7.66764V12.1121M10.7778 7.66764V12.1121" stroke="#828282" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>
                <span>${language.button.remove}</span>
              </button>
              ${
                isBundleGift
                  ? ""
                  : `
              <button 
                class="cart-line-item__controls-button cart-line-item__controls-button--wishlist product_${productId}"
                data-with-epi="true"
                data-swaction="addToWishlist"
                data-product-id="${productId}"
                data-variant-id="${variantID}"
                data-product-url="${productUrl}"
              >
                <svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M8.99452 2.70963C7.39504 0.839696 4.7278 0.336691 2.72376 2.04899C0.719715 3.76128 0.437574 6.62416 2.01136 8.6493C3.31985 10.3331 7.27982 13.8843 8.57768 15.0336C8.72288 15.1622 8.79549 15.2265 8.88017 15.2518C8.95408 15.2738 9.03496 15.2738 9.10887 15.2518C9.19356 15.2265 9.26616 15.1622 9.41136 15.0336C10.7092 13.8843 14.6692 10.3331 15.9777 8.6493C17.5515 6.62416 17.3038 3.74327 15.2653 2.04899C13.2268 0.354702 10.594 0.839696 8.99452 2.70963Z" stroke="#828282" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>
                <span>${language.button.moveToWishlist}</span>
              </button>`
              }
            </div>
          </div>

          <div class="cart-line-item__details">
            <div class="cart-line-item__stepper js--cart-line-item-stepper">
              ${quantitySelector}
            </div>
            ${productPrice}
          </div>

          ${productDiscounts}

        </div>

        <input class="cart-line-item__over-limit-input" type="checkbox" ${productOverLimit ? "checked" : ""}>

        ${explicitOrderLimit ? productOverLimitMessage : ""}

      </div>
    `;
  };

  const cartLineItemProductDiscounts = (lineItem = {}) => {
    let template = "";

    if (lineItem.discounts.length) {
      template = `
        <div class="cart-line-item__discounts">
          ${lineItem.discounts.map((discount) => `<span class="cart-line-item__discount-title">+ ${discount.title}</span>`).join("")}
        </div>
      `;
    }

    return template;
  };

  const cartLineItemProductExtras = (lineItem = {}, cartAdditionalData = {}) => {
    let template = "";
    const isCannabis = lineItem.properties?._is_cannabis ?? false;
    const dceValue = settings.isSiteFrench ? (lineItem.properties?._fr_dce_value ?? "") : (lineItem.properties?._dce_value ?? "");
    const optionsWithValues = lineItem?.options_with_values || [];
    const packSize = lineItem.properties?._pack_size ?? "";
    const packSizeUom = lineItem.properties?._pack_size_uom ?? "";
    const showCannabisExtras = !!(dceValue && packSize && packSizeUom);

    LOGGING &&
      console.log("[ cartLineItemProductExtras() ] details...", {
        isCannabis,
        optionsWithValues,
        dceValue,
        packSize,
        packSizeUom,
        showCannabisExtras,
        cartAdditionalData,
      });

    const components = cartAdditionalData?.[lineItem?.id]?.item_components || [];
    if (components.length > 0) {
      const variant_id = lineItem.variant_id;
      const hideLabel = settings.isSiteFrench ? "Masquer les articles" : "Hide items";
      const showLabel = settings.isSiteFrench ? "Afficher les articles" : "Show items";
      const lineItemKey = lineItem.key || variant_id;

      template = `
        <div class="cart-line-item__components">
          <button
            class="cart-line-item__components-toggle js--cart-components-toggle"
            type="button"
            aria-expanded="true"
            aria-controls="cart-line-item-components-${lineItemKey}"
          >
            <span class="cart-line-item__components-toggle-text" data-hide-text="${hideLabel}" data-show-text="${showLabel}">${hideLabel}</span>
            <span class="cart-line-item__components-caret" aria-hidden="true">
              <svg aria-hidden="true" focusable="false" viewBox="0 0 10 6">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
              </svg>
            </span>
          </button>
          <div class="cart-line-item__components-container" id="cart-line-item-components-${lineItemKey}">
            ${components.map((component) => {
              const { image, properties, options_with_values } = component;
              const baseTitle = AuroraUtils.getResolvedProductTitleFromBundleComponent(component, {
                isSiteFrench: settings.isSiteFrench,
              });
              const componentTitle = `${component.quantity} × ${baseTitle}`;
              const isCannabis = properties?._is_cannabis ?? false;
              const dceValue = settings.isSiteFrench ? (properties?._fr_dce_value ?? "") : (properties?._dce_value ?? "");
              const packSize = properties?._pack_size ?? "";
              const packSizeUom = properties?._pack_size_uom ?? "";
              const subtitleText = isCannabis
                ? `${packSize} ${packSizeUom} • ${dceValue}`
                : options_with_values.map((option) => `<span>${option.name}: ${option.value}</span>`).join("");
              return `<div class="cart-line-item__components-item"><img loading="lazy" src="${image}" alt="${baseTitle}" />
                <div class="cart-line-item__components-item-title-container">
                  <span class="cart-line-item__components-item-title">${componentTitle}</span>
                  <span class="cart-line-item__components-item-subtitle">${subtitleText}</span>
                </div>
              </div>`
            }).join("")}
          </div>
        </div>
      `;
      
    } else if (isCannabis) {
      template = showCannabisExtras ? `<span class="cart-line-item__product-extras">${packSize} ${packSizeUom} • ${dceValue}</span>` : ``;
    } else {
      if (optionsWithValues.length) {
        template = `
          <span class="cart-line-item__product-extras">
            ${optionsWithValues.map((option) => `<span>${option.name}: ${option.value}</span>`).join("")}
          </span>
        `;
      }
    }

    return template;
  };

  const cartLineItemProductOverLimitMessage = (lineItem = {}, lineItemAdditionalData = {}, overLimit = false, quantity = 0, orderLimit = 0) => {
    if (!orderLimit) return "";

    const orderLimitLocales = window.theme?.locales?.restrictions?.product?.orderLimit || {};

    const difference = Math.max(0, quantity - orderLimit);
    const template = difference === 0 ? orderLimitLocales.atLimit : orderLimitLocales.overLimit;

    if (!template) return "";

    const message = template
      .replace(/\[\[LIMIT\]\]/g, orderLimit)
      .replace(/\[\[DIFFERENCE\]\]/g, difference);

    return `<div class="cart-line-item__over-limit-warning${overLimit ? " over-limit" : ""}">${message}</div>`;
  };

  const cartLineItemProductPrice = (lineItem = {}) => {
    const finalLinePrice = lineItem.final_line_price;
    const productKey = lineItem.key;
    const originalLinePrice = lineItem.original_line_price;

    return finalLinePrice < originalLinePrice
      ? `<div class="cart-line-item__price" id="cart-line-item-price--${productKey}">${AuroraUtils.formatMoney(finalLinePrice / 100)}<span>${AuroraUtils.formatMoney(originalLinePrice / 100)}</span></div>`
      : `<div class="cart-line-item__price" id="cart-line-item-price--${productKey}">${AuroraUtils.formatMoney(finalLinePrice / 100)}</div>`;
  };

  const siteTourStep = (index = 0, step = {}, last = false) => {
    console.log("[ siteTourStep() init ::", { index, step, last });

    const div = document.createElement("div");
    const stepPrev = index - 1;
    const stepNext = index + 1;
    const language = {
      button: {
        close: settings.isSiteFrench ? "Fermer" : "Close",
        dontShow: settings.isSiteFrench ? "Ne me montrez plus ça" : "Don't show me this again",
        exit: settings.isSiteFrench ? "Quitter la visite" : "Exit tour",
        next: settings.isSiteFrench ? "Fonctionnalité suivante" : "Next",
        no: settings.isSiteFrench ? "Non merci" : "No",
        prev: settings.isSiteFrench ? "Fonctionnalité précédente" : "Previous feature",
        yes: settings.isSiteFrench ? "Oui SVP" : "Yes",
      },
    };

    div.classList.add("aur-popup-inner");
    div.setAttribute("data-step", index);

    if (last) {
      // last step
      div.innerHTML = `
        <div class="aur-popup-header">
          <p class="aur-close-tour js--close-site-tour"><img src="${assets.icon.close}">${language.button.exit}</p>
        </div>
        <div class="aur-popup-content">
          <p>${step["content"]}</p>
          <div class="aur-button-group">
            <a href="#" class="aur-tour-nav-button aur-tour-nav-button-prev js--site-tour-navigation-button" data-step="${stepPrev}"><img src="${assets.icon.arrowPrev}">${language.button.prev}</a>
            <a href="#" class="aur-button aur-tour-nav-button  aur-close-tour js--close-site-tour">${language.button.close}</a>
          </div>
        </div>
      `;
    } else {
      if (0 === index) {
        // first step
        div.classList.add("aur-popup-inner-open");
        div.innerHTML = `
          <div class="aur-popup-header">
            <img src="${assets.icon.closeBlue}" class="aur-popup-close aur-close-tour js--close-site-tour" />
          </div>
          <div class="aur-popup-content">
            <p>${step["content"]}</p>
            <div class="aur-button-group">
              <a href="#" class="aur-button aur-tour-nav-button js--site-tour-navigation-button" data-step="1">${language.button.yes}</a>
              <a href="#" class="aur-button aur-button-light aur-close-tour js--close-site-tour">${language.button.no}</a>
            </div>
            <p class="aur-close-tour aur-dont-show-tour js--close-site-tour"><img src="${assets.icon.eyeOff}">${language.button.dontShow}</p>
          </div>
        `;
      } else {
        // in between steps
        div.innerHTML = `
          <div class="aur-popup-header">
            <p class="aur-close-tour js--close-site-tour"><img src="${assets.icon.close}">${language.button.exit}</p>
          </div>
          <div class="aur-popup-content">
            <p>${step["content"]}</p>
            <div class="aur-button-group">
              <a href="#" class="aur-tour-nav-button aur-tour-nav-button-prev js--site-tour-navigation-button" data-step="${stepPrev}"><img src="${assets.icon.arrowPrev}">${language.button.prev}</a>
              <a href="#" class="aur-button aur-tour-nav-button js--site-tour-navigation-button" data-step="${stepNext}">${language.button.next}<img src="${assets.icon.arrowNext}"></a>
            </div>
          </div>
        `;
      }
    }

    return div;
  };

  return {
    cartLineItem,
    siteTourStep,
  };
})();
